import React, { useMemo } from 'react';
import { View, Pressable, StyleSheet, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import ExploreCategoryDropdown from '@/components/Dropdowns/ExploreCategoryDropdown';
import ExploreSortDropdown from '@/components/Dropdowns/ExploreSortDropdown';
import { CATALOG_VIEW_MODES } from '@/components/Catalog/CatalogBrowseShell';
import { CAPABILITY_COLORS } from '@/utils/model_catalog';

const isWeb = Platform.OS === 'web';

const MEDIA_TABS = [
  { id: null, labelKey: 'explore.allMedia', icon: 'grid-2', color: '#3B82F6' },
  { id: 'image', labelKey: 'explore.images', icon: 'image', color: CAPABILITY_COLORS.image },
  { id: 'video', labelKey: 'explore.videos', icon: 'video', color: CAPABILITY_COLORS.video },
  { id: 'music', labelKey: 'explore.music', icon: 'music', color: CAPABILITY_COLORS.audio },
  { id: '3d', labelKey: 'explore.models3d', icon: 'cube', color: CAPABILITY_COLORS['3d'] },
];

function translateLabel(t, key, fallback) {
  const value = t(key);
  return value === key ? fallback : value;
}

const MediaPill = React.memo(({ def, label, active, onPress, colors }) => {
  const accent = def.color;
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      accessibilityLabel={label}
      style={({ pressed }) => [
        styles.mediaPill,
        {
          backgroundColor: active ? `${accent}1F` : colors.solid_color_primary,
          borderColor: active ? accent : colors.border_color_secondary,
          opacity: pressed ? 0.88 : 1,
        },
        isWeb && { cursor: 'pointer' },
      ]}
    >
      <Text
        size="xs"
        weight={active ? 'bold' : 'semibold'}
        style={{ color: active ? accent : colors.text_color_primary }}
        numberOfLines={1}
      >
        {label}
      </Text>
    </Pressable>
  );
});
MediaPill.displayName = 'ExploreMobileMediaPill';

function ExploreMobileToolbar({
  filterMediaType,
  onMediaTypeChange,
  viewMode,
  onViewModeChange,
  categories,
  activeCategorySlug,
  onCategoryChange,
  activeSort,
  filterFavorites,
  onSortChange,
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();

  const mediaItems = useMemo(() => MEDIA_TABS.map((def) => ({
    def,
    label: translateLabel(t, def.labelKey, def.id || 'All'),
    active: filterMediaType === def.id,
  })), [t, filterMediaType]);

  const listA11y = translateLabel(t, 'pages.common.list', 'List');
  const gridA11y = translateLabel(t, 'pages.common.grid', 'Grid');

  return (
    <View
      style={[
        styles.shell,
        {
          backgroundColor: colors.solid_color_block,
          borderColor: colors.border_color_secondary,
        },
      ]}
    >
      <View style={styles.mediaRow}>
        {mediaItems.map(({ def, label, active }) => (
          <MediaPill
            key={def.id ?? 'all'}
            def={def}
            label={label}
            active={active}
            onPress={() => onMediaTypeChange?.(def.id)}
            colors={colors}
          />
        ))}
      </View>

      <View style={styles.actionsRow}>
        <View
          style={[
            styles.viewToggle,
            { backgroundColor: colors.solid_color_primary, borderColor: colors.border_color_secondary },
          ]}
        >
          <Pressable
            onPress={() => onViewModeChange?.(CATALOG_VIEW_MODES.LIST)}
            accessibilityLabel={listA11y}
            accessibilityRole="button"
            accessibilityState={{ selected: viewMode === CATALOG_VIEW_MODES.LIST }}
            style={[
              styles.viewToggleBtn,
              { backgroundColor: viewMode === CATALOG_VIEW_MODES.LIST ? colors.solid_color_tertiary : 'transparent' },
              isWeb && { cursor: 'pointer' },
            ]}
          >
            <Icon
              name="bars"
              size={12}
              color={viewMode === CATALOG_VIEW_MODES.LIST ? colors.text_color_primary : colors.text_color_secondary}
            />
          </Pressable>
          <Pressable
            onPress={() => onViewModeChange?.(CATALOG_VIEW_MODES.GRID)}
            accessibilityLabel={gridA11y}
            accessibilityRole="button"
            accessibilityState={{ selected: viewMode === CATALOG_VIEW_MODES.GRID }}
            style={[
              styles.viewToggleBtn,
              { backgroundColor: viewMode === CATALOG_VIEW_MODES.GRID ? colors.solid_color_tertiary : 'transparent' },
              isWeb && { cursor: 'pointer' },
            ]}
          >
            <Icon
              name="grid-2"
              size={12}
              color={viewMode === CATALOG_VIEW_MODES.GRID ? colors.text_color_primary : colors.text_color_secondary}
            />
          </Pressable>
        </View>

        <View style={styles.chipSlot}>
          <ExploreCategoryDropdown
            variant="toolbarChip"
            categories={categories}
            activeCategorySlug={activeCategorySlug}
            onChangeCategory={onCategoryChange}
          />
        </View>
        <View style={styles.chipSlot}>
          <ExploreSortDropdown
            variant="toolbarChip"
            activeSort={activeSort}
            filterFavorites={filterFavorites}
            onChangeSort={onSortChange}
          />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  shell: {
    width: '100%',
    borderRadius: 12,
    borderWidth: 1,
    paddingVertical: 10,
    paddingHorizontal: 10,
    gap: 10,
  },
  mediaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    width: '100%',
  },
  mediaPill: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    height: 32,
    paddingHorizontal: 4,
    borderRadius: 16,
    borderWidth: 1,
    minWidth: 0,
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    width: '100%',
  },
  viewToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    padding: 3,
    borderRadius: 10,
    borderWidth: 1,
    flexShrink: 0,
  },
  viewToggleBtn: {
    width: 34,
    height: 30,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 7,
  },
  chipSlot: {
    flex: 1,
    minWidth: 0,
  },
});

export default React.memo(ExploreMobileToolbar);
